import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { pushOverlay, popOverlay, isTopOverlay } from './overlayStack'
import { dialogVerlassen } from '../lib/dialogAbgang'
import { createPortal } from 'react-dom'

/** True inside a mounted <Modal> — Dropdown/MultiSelect read this to default
 *  their menu to portal rendering (see FIX 3, DESIGN.md §14): an in-flow
 *  `absolute` menu gets clipped by the Modal body's own `overflow-y-auto`. */
export const ModalContext = createContext(false)

/** True inside the Schulportal (SchoolApp provides it): every <Modal> there gets the prototype spring-in / exit motion without passing `spring`.
 *  Other modules keep the instant open and close. An explicit `spring` prop always wins. */
export const SpringContext = createContext(false)

/** The Modal's own glass footer DOM node (null outside a Modal) — separate
 *  from ModalContext above, which Dropdown/MultiSelect read for a different
 *  purpose and whose boolean semantics stay untouched. `ModalFooter` portals
 *  its children into this node when it's set. */
const ModalFooterContext = createContext<HTMLElement | null>(null)

/**
 * Renders its children into the Modal's glass footer (see `footer` prop on
 * `Modal` for the alternative, simpler path). Use this when the footer
 * content lives deeper in the tree than the `Modal` call site (e.g. a step
 * component that owns its own "weiter/zurück" actions). Falls back to
 * rendering inline when used outside any `Modal`. If both the `footer` prop
 * and `ModalFooter` are used on the same Modal, both render — prop first.
 */
export function ModalFooter({ children }: { children: ReactNode }) {
  const footerEl = useContext(ModalFooterContext)
  if (!footerEl) return <>{children}</>
  return createPortal(children, footerEl)
}

/** The exact close "X" the default Modal header uses. Exported so a custom
 *  `header` can reuse it verbatim instead of re-styling its own. */
export function ModalCloseButton({ onClick, strong = false }: { onClick: () => void; strong?: boolean }) {
  return (
    <button onClick={onClick} aria-label="Schließen"
      className={strong
        ? 'grid h-11 w-11 -m-2 place-items-center rounded-full text-on-surface hover:bg-glass-a transition-colors flex-shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-primary/60'
        : 'p-1.5 -m-1.5 rounded-lg text-on-surface-variant hover:text-on-surface hover:bg-glass-a transition-colors flex-shrink-0'}>
      <span className="material-symbols-outlined !text-xl">close</span>
    </button>
  )
}

interface ModalProps {
  open: boolean
  onClose: () => void
  /** Title shown in the modal header. Either `title` or `header` must be provided. */
  title?: string
  /** Optional subtitle (e.g. entity ID, context). Ignored when `header` is provided. */
  subtitle?: ReactNode
  /**
   * Custom header content that replaces the default title/subtitle/close-button row.
   * Consumer owns the full header area and is responsible for providing a close affordance.
   * Use this for branded modals (avatar tile, status badge, multi-line context).
   */
  header?: ReactNode
  /** Modal body. */
  children: ReactNode
  /** Right-aligned footer actions (Cancel / Save buttons). */
  footer?: ReactNode
  /** Width preset: sm = 28rem, md = 36rem (default), lg = 48rem, xl = 64rem, 2xl = 70.25rem, full = 96vw. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
  /** Suppress the default header + close button entirely. Backdrop click and Escape still close. */
  noHeader?: boolean
  /**
   * Accessible name for screen readers when `noHeader` suppresses the title
   * row (so there's no on-screen text Modal itself can point `aria-labelledby`
   * at). Ignored when a `title`/`header` is present — those already label the
   * dialog. Optional; a `noHeader` modal without one still opens, just without
   * an announced name (review finding, 18.09.2026).
   */
  ariaLabel?: string
  /** Body background. `default` = none (the dialog card itself is the surface).
   *  `tonal` = a `bg-surface-container-low` panel inside the card — for split-screen
   *  comparison modals, DESIGN.md §16. */
  bodyTone?: 'default' | 'tonal'
  /** Spring entry (y 40, scale .85, rotateX 25, overshoot, 600 ms) for the Schulportal stage (DESIGN.md 18); off under reduced motion. */
  spring?: boolean
  /** With `spring` (Schulportal only) a close request plays the prototype exit (scale .9, y 20, fade, 220 ms) before `onClose`
   *  runs; other modules keep the instant close. `leaving` = true runs that exit from outside (e.g. after a save) and then `onLeft`.
   *  When `onLeft` is given, the parent owns the exit: `onClose` runs at once and must turn `leaving` on. */
  leaving?: boolean
  onLeft?: () => void
  /** Close requests (Esc, backdrop, X) are ignored while true (a save is running). */
  blocked?: boolean
  /** A dialog opened ON TOP of another dialog (prototype layer 2, e.g. the chip signature): darker backdrop (.45 instead of .35). */
  layer2?: boolean
  /** `end` = a side sheet (M30 QM visit sheet, mockup `.dialog.drawer`): full height at the right edge from 640 px up, a bottom sheet on phones.
   *  Same motion, same focus handling. Default `center`. */
  align?: 'center' | 'end'
  /** M30 QM dialogs only: a 44 px close button in full text colour and a subtitle in full text colour at 14 px (contrast, touch size), and the
   *  mockup surface: a solid panel (surface-container-lowest, 32 px radius, no inner second box, no blur; the blur stays on the backdrop).
   *  Default off, so Schulportal and all other dialogs stay as they are. */
  strong?: boolean
}

// Widths of the prototype card: plain `.dialog` min(100%, 460), `.dialog.wide` min(100%, 560); larger presets are the data dialogs of the office.
const SIZES = { sm: 'max-w-[460px]', md: 'max-w-[560px]', lg: 'max-w-3xl', xl: 'max-w-5xl', '2xl': 'max-w-[1124px]', full: 'max-w-[96vw]' } as const
// default: -m/p 5px gives the 3 px focus ring + 2 px offset room inside the scroll body; tonal already has its own padding.
const BODY_BG = { default: '-m-[5px] p-[5px]', tonal: 'rounded-2xl bg-surface-container-low px-4 py-3' } as const
// Prototype `.dialog .actions .btn`: pill, min 54 px, 17 px / 600. Narrow dialogs (sm, md) stack the buttons full width; wide data dialogs keep a right-aligned row.
const FOOTER_BTN = '[&_button]:min-h-[54px] [&_button]:rounded-full [&_button]:text-[17px] [&_button]:font-semibold [&_button]:px-6'

/**
 * The single canonical Modal/Dialog. NEVER hand-roll a portal+backdrop. See DESIGN.md § 8c.
 *
 * Esc closes; click outside content closes; body scroll-locks while open.
 */
export function Modal({ open, onClose, title, subtitle, header, children, footer, size = 'md', noHeader = false, ariaLabel, bodyTone = 'default', spring: springProp, leaving = false, onLeft, blocked = false, layer2 = false, align = 'center', strong = false }: ModalProps) {
  // Depth among all currently-open overlays (Modal + Drawer share the stack)
  // — 0 for a standalone Modal (keeps the original z-70), 1+ for one opened
  // on top of another (e.g. the "Löschen" confirm opened from an "bearbeiten"
  // Modal, or a drawer's own delete-confirm), so the later one always wins.
  const springCtx = useContext(SpringContext)
  const spring = springProp ?? springCtx
  const [depth, setDepth] = useState(0)
  const titleId = useId()
  const shellRef = useRef<HTMLDivElement>(null)
  // Portal target for <ModalFooter>; null until the footer element itself
  // mounts (ref callback), so ModalFooter falls back to inline on that very
  // first render and then re-renders portaled once this is set.
  const [footerEl, setFooterEl] = useState<HTMLElement | null>(null)
  // Der NEUESTE onClose in einer Referenz: viele Eltern reichen bei jedem Render eine neue Pfeilfunktion herein
  // (der Editor-Drawer bei jedem Takt seiner Abfragen). Stünde onClose in den Abhängigkeiten des Effekts unten, würde
  // dieser bei jedem Eltern-Render neu registrieren — das Modal rückte im overlayStack wieder nach oben, ein darüber
  // offenes („Groß ansehen" über dem Assistenten) fiele dahinter, und Escape schlösse das falsche.
  const onCloseRef = useRef(onClose)
  useLayoutEffect(() => { onCloseRef.current = onClose })

  // Schulportal exit motion (spring only): play it, then run `danach`. Without spring or under reduced motion: at once.
  const backdropRef = useRef<HTMLDivElement>(null)
  const geht = useRef(false)
  const abgang = (danach: () => void) => {
    if (!spring) { danach(); return }
    if (geht.current) return
    const a = dialogVerlassen(shellRef.current, backdropRef.current)
    if (!a) { danach(); return }
    geht.current = true
    void a.fertig.then(() => {
      danach()
      // If the parent kept the dialog open (it refused to close), bring it back instead of leaving it invisible.
      window.setTimeout(() => { a.abbrechen(); geht.current = false }, 50)
    })
  }
  const fordernRef = useRef<() => void>(() => {})
  useLayoutEffect(() => { fordernRef.current = () => {
    if (blocked) return
    // With `onLeft` the parent owns the exit (it turns `leaving` on from onClose), so do not play it twice.
    if (onLeftRef.current) onCloseRef.current()
    else abgang(() => onCloseRef.current())
  } })
  const onLeftRef = useRef(onLeft)
  useLayoutEffect(() => { onLeftRef.current = onLeft })
  useEffect(() => {
    if (leaving) abgang(() => onLeftRef.current?.())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaving])

  useLayoutEffect(() => {
    if (!open) return
    // Only the topmost overlay reacts to Escape (see overlayStack.ts): a Modal
    // stacked on a <Drawer> — e.g. the Assistent, §6.9 — must not close the
    // Drawer underneath, and an open Dropdown/MultiSelect menu (on the stack as
    // 'menu') takes the first Escape for itself.
    const overlayId = Symbol('modal')
    setDepth(pushOverlay(overlayId, 'modal'))
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && isTopOverlay(overlayId)) fordernRef.current() }
    document.addEventListener('keydown', onKey)
    // Der Body-Scroll wird im overlayStack gezählt (pushOverlay/popOverlay), nicht je Modal.
    // Review finding, 18.09.2026: the modal is portaled to the end of
    // document.body and can open asynchronously (e.g. a background 401)
    // while focus sits elsewhere (mid-form). Move focus in so a
    // keyboard-only user lands inside it instead of stuck wherever they
    // were; the shell itself is the fallback when there's nothing
    // focusable (e.g. a busy/spinner noHeader modal).
    const previouslyFocused = document.activeElement as HTMLElement | null
    // ABER: nur, wenn nicht schon etwas IM Dialog den Fokus hat. React setzt
    // autoFocus waehrend des Commits, also bevor dieser Layout-Effekt des
    // Elternteils laeuft — ein blindes focus() auf das erste fokussierbare
    // Element (meist der Schliessen-Knopf im Kopf) haette genau die Eingaben
    // bestohlen, die ihn brauchen: das Scanner-Feld in BarcodeScanModal und
    // das Suchfeld in RoleAdmin. Steht der Fokus schon drin, bleibt er.
    const schonDrin = shellRef.current?.contains(document.activeElement)
    if (!schonDrin) {
      const target = shellRef.current?.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? shellRef.current
      target?.focus()
    }
    return () => {
      popOverlay(overlayId)
      document.removeEventListener('keydown', onKey)
      previouslyFocused?.focus?.()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <ModalContext.Provider value={true}>
      <ModalFooterContext.Provider value={footerEl}>
        <div
          // Dimmed + blurred backdrop: the page behind softly darkens and
          // defocuses, so the modal stands out without hiding context. DESIGN.md §14.
          // Base z-70 sits above the Sidebar (z-60); a Modal stacked on another
          // overlay steps up by 2 per depth so it always wins.
          ref={backdropRef}
          className={`fixed inset-0 flex backdrop-blur-[6px] animate-[fade-in_0.25s_cubic-bezier(0.25,0.46,0.45,0.94)] motion-reduce:animate-none [:root[data-bewegung=aus]_&]:animate-none ${layer2 ? 'bg-[rgba(15,23,42,0.45)]' : 'bg-[rgba(15,23,42,0.35)]'} ${align === 'end' ? 'items-end justify-center p-0 sm:items-stretch sm:justify-end sm:p-3' : 'items-center justify-center p-4'}`}
          style={{ zIndex: 70 + depth * 2 }}
          onClick={e => { if (e.target === e.currentTarget) fordernRef.current() }}
        >
          {/* ONE card (prototype .dialog, M27 T3 spec D): solid card surface, radius 32, 1 px hairline, cast shadow, no glass inside.
              Header and footer stay fixed; only the body scrolls when the content is taller than the window. */}
          <div
            ref={shellRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={!noHeader && !header ? titleId : undefined}
            aria-label={(noHeader || header) ? ariaLabel : undefined}
            tabIndex={-1}
            className={`dialog w-full ${SIZES[size]} ${align === 'end' ? 'max-h-[88dvh] rounded-b-none sm:h-full sm:max-h-none sm:rounded-b-[32px]' : 'max-h-[calc(100dvh-32px)]'} flex flex-col overflow-hidden rounded-[32px] bg-card shadow-[inset_0_0_0_1px_var(--color-line),0_40px_80px_-24px_rgba(0,0,0,0.45)] outline-none${strong ? ' [&_label.uppercase]:text-[14px] [&_label.uppercase]:font-semibold [&_label.uppercase]:normal-case [&_label.uppercase]:tracking-normal [&_label.uppercase]:text-on-surface' : ''}${spring ? ' sp-dialog-in' : ''}`}
          >
            <div className="dlg-in flex min-h-0 flex-1 flex-col gap-3.5 overflow-hidden px-[28px] py-[28px] max-[520px]:px-[18px] max-[520px]:py-[22px]">
              {!noHeader && (header ? (
                <div className="flex-shrink-0">{header}</div>
              ) : (
                <header className="flex flex-shrink-0 items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h2 id={titleId} className="font-headline text-[22px] max-[520px]:text-[20px] font-extrabold leading-[1.2] text-on-surface">{title}</h2>
                    {subtitle && <p className={strong ? 'text-sm text-on-surface mt-0.5' : 'text-xs text-on-surface-variant mt-0.5'}>{subtitle}</p>}
                  </div>
                  <ModalCloseButton strong={strong} onClick={() => fordernRef.current()} />
                </header>
              ))}
              <div className={`${BODY_BG[bodyTone]} min-h-0 flex-1 overflow-y-auto`}>{children}</div>
              {/* Always rendered (not gated on `footer`) so a <ModalFooter> used deeper in `children` has somewhere to portal into even when
                  no `footer` prop is passed; `empty:hidden` collapses it back to nothing when neither is used. */}
              <footer ref={setFooterEl} className={`mt-1.5 flex flex-shrink-0 empty:hidden ${FOOTER_BTN} ${size === 'sm' || size === 'md' ? 'flex-col gap-2.5 [&_button]:w-full' : 'flex-wrap justify-end gap-2.5'}`}>
                {footer}
              </footer>
            </div>
          </div>
        </div>
      </ModalFooterContext.Provider>
    </ModalContext.Provider>,
    document.body,
  )
}
